import { MONTH_SHORT, shiftDateKey, WEEK_ORDER } from "./time.ts";

/**
 * The Book tab's calendar as data: which days it can show, which day is open, and what each day
 * says. Pure and keyed by `yyyy-MM-dd` calendar keys in the business's timezone, so the web and a
 * later phone version draw the same days with the same words.
 *
 * The week view is the member home's strip: seven days that start today and page by whole weeks.
 * The month view is the business month, laid out in the business week (Saturday first). Both
 * mark each day by what a member can do on it: open (at least one session with a free spot), closed
 * (sessions, but all full or started), or nothing.
 */

/**
 * How far ahead the Book tab reads open sessions. The session job makes eight weeks ahead
 * (`SESSION_HORIZON_DAYS`, 56 by default), so a longer window only shows empty days.
 */
export const BOOK_WINDOW_DAYS = 56;

/** The last week page of the week view: page 0 starts today, page 7 holds the last window day. */
export const BOOK_LAST_WEEK_PAGE = Math.ceil(BOOK_WINDOW_DAYS / 7) - 1;

export type BookCalendarView = "week" | "month";

/** What a member can do on a day. */
export type BookDayState = "open" | "closed" | "none";

/** The sessions on one day: with a free spot (`open`) and without (`closed`). */
export type BookDayCount = { open: number; closed: number };

const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEKDAY_LONG = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTH_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function utc(key: string): Date {
  return new Date(`${key}T00:00:00.000Z`);
}

/** Whole calendar days from `from` to `to` (negative when `to` is earlier). */
export function daysBetweenKeys(from: string, to: string): number {
  return Math.round((utc(to).getTime() - utc(from).getTime()) / 86_400_000);
}

/** The first and the last day the Book tab reads. */
export function bookWindow(todayKey: string): { first: string; last: string } {
  return { first: todayKey, last: shiftDateKey(todayKey, BOOK_WINDOW_DAYS - 1) };
}

export function isInBookWindow(todayKey: string, key: string): boolean {
  const offset = daysBetweenKeys(todayKey, key);
  return offset >= 0 && offset < BOOK_WINDOW_DAYS;
}

/** Counts the sessions per day. Each row is one session: its day and whether it has a free spot. */
export function bookDayCounts(rows: Iterable<{ key: string; bookable: boolean }>): Map<string, BookDayCount> {
  const counts = new Map<string, BookDayCount>();
  for (const row of rows) {
    const count = counts.get(row.key) ?? { open: 0, closed: 0 };
    if (row.bookable) count.open += 1;
    else count.closed += 1;
    counts.set(row.key, count);
  }
  return counts;
}

export function bookDayState(count: BookDayCount | undefined): BookDayState {
  if (!count) return "none";
  if (count.open > 0) return "open";
  return count.closed > 0 ? "closed" : "none";
}

/** The week page that holds `key`: 0 for today and the next 6 days. */
export function bookWeekPage(todayKey: string, key: string): number {
  const page = Math.floor(daysBetweenKeys(todayKey, key) / 7);
  return Math.min(Math.max(0, page), BOOK_LAST_WEEK_PAGE);
}

/** The seven days of a week page: today plus whole weeks, like the member home's strip. */
export function bookWeekKeys(todayKey: string, page: number): string[] {
  const start = shiftDateKey(todayKey, page * 7);
  return Array.from({ length: 7 }, (_, index) => shiftDateKey(start, index));
}

/** `yyyy-MM` of a day key. */
export function monthKeyOf(key: string): string {
  return key.slice(0, 7);
}

/** Moves a `yyyy-MM` month key by whole months. */
export function shiftMonthKey(month: string, by: number): string {
  const [year, number] = month.split("-").map(Number);
  const date = new Date(Date.UTC(year!, number! - 1 + by, 1));
  return date.toISOString().slice(0, 7);
}

/** Whole months from `from` to `to`, both `yyyy-MM`: 1 from September to October, -1 back. */
export function monthsBetween(from: string, to: string): number {
  const [fromYear, fromNumber] = from.split("-").map(Number);
  const [toYear, toNumber] = to.split("-").map(Number);
  return (toYear! - fromYear!) * 12 + (toNumber! - fromNumber!);
}

/** The months the month view can show: the month of today to the month of the last window day. */
export function bookMonths(todayKey: string): string[] {
  const last = monthKeyOf(bookWindow(todayKey).last);
  const months = [monthKeyOf(todayKey)];
  while (months[months.length - 1]! < last) months.push(shiftMonthKey(months[months.length - 1]!, 1));
  return months;
}

/**
 * The month as rows of seven, in the business week's order. A cell outside the month is `null`, so
 * the grid keeps its columns and no day of another month looks like it belongs here.
 */
export function monthGridKeys(month: string, weekOrder: readonly number[] = WEEK_ORDER): (string | null)[][] {
  const first = `${month}-01`;
  const lead = weekOrder.indexOf(utc(first).getUTCDay());
  const length = daysBetweenKeys(first, `${shiftMonthKey(month, 1)}-01`);
  const cells: (string | null)[] = [
    ...Array.from({ length: lead }, () => null),
    ...Array.from({ length }, (_, index) => shiftDateKey(first, index)),
  ];
  while (cells.length % 7 !== 0) cells.push(null);
  return Array.from({ length: cells.length / 7 }, (_, row) => cells.slice(row * 7, row * 7 + 7));
}

/** "October 2026". */
export function monthTitle(month: string): string {
  const [year, number] = month.split("-").map(Number);
  return `${MONTH_LONG[number! - 1]} ${year}`;
}

/** "Sat", "Sun" ... in the business week's order, for the month view's column heads. */
export function weekdayHeads(weekOrder: readonly number[] = WEEK_ORDER): string[] {
  return weekOrder.map((weekday) => WEEKDAY_SHORT[weekday]!);
}

/** "Sat" and "4" for a day tile. */
export function bookDayFace(key: string): { weekday: string; dayOfMonth: string } {
  const date = utc(key);
  return { weekday: WEEKDAY_SHORT[date.getUTCDay()]!, dayOfMonth: String(date.getUTCDate()) };
}

/** "Today", "Tomorrow", or "Sat 4 Oct": the heading over the day's sessions. */
export function bookDayTitle(todayKey: string, key: string): string {
  const offset = daysBetweenKeys(todayKey, key);
  if (offset === 0) return "Today";
  if (offset === 1) return "Tomorrow";
  const date = utc(key);
  return `${WEEKDAY_SHORT[date.getUTCDay()]} ${date.getUTCDate()} ${MONTH_SHORT[date.getUTCMonth()]}`;
}

/** What a screen reader says for a day: "Today, Saturday 4 October, 2 sessions open, 1 full or closed". */
export function bookDaySpoken(todayKey: string, key: string, count: BookDayCount | undefined): string {
  const date = utc(key);
  const name = `${key === todayKey ? "Today, " : ""}${WEEKDAY_LONG[date.getUTCDay()]} ${date.getUTCDate()} ${MONTH_LONG[date.getUTCMonth()]}`;
  const open = count?.open ?? 0;
  const closed = count?.closed ?? 0;
  if (open === 0 && closed === 0) return `${name}, no sessions`;
  if (open === 0) return `${name}, ${plural(closed, "session")}, none open`;
  return `${name}, ${plural(open, "session")} open${closed > 0 ? `, ${closed} full or closed` : ""}`;
}

/** The first of `keys` with a session a member can book, else the first with any session. */
export function firstBookableDay(keys: readonly string[], counts: ReadonlyMap<string, BookDayCount>): string | null {
  return (
    keys.find((key) => bookDayState(counts.get(key)) === "open") ??
    keys.find((key) => bookDayState(counts.get(key)) !== "none") ??
    null
  );
}

/** The next day after `key`, inside the window, with a session that has a free spot. */
export function nextOpenDay(todayKey: string, key: string, counts: ReadonlyMap<string, BookDayCount>): string | null {
  const { last } = bookWindow(todayKey);
  const after = [...counts.entries()]
    .filter(([day, count]) => day > key && day <= last && count.open > 0)
    .map(([day]) => day)
    .sort();
  return after[0] ?? null;
}

/**
 * The day the calendar opens on when a member picks none (or picked one outside the window): the
 * first day with a free spot, else the first with any session, else today.
 */
export function defaultBookDay(todayKey: string, counts: ReadonlyMap<string, BookDayCount>): string {
  const { last } = bookWindow(todayKey);
  const days = [...counts.keys()].filter((key) => key >= todayKey && key <= last).sort();
  return firstBookableDay(days, counts) ?? todayKey;
}

/**
 * The day to open after a page or a month changes: the first day of `keys` (inside the window) a
 * member can book, else the first with any session, else the first day of `keys` in the window.
 */
export function landingDay(todayKey: string, keys: readonly string[], counts: ReadonlyMap<string, BookDayCount>): string {
  const inWindow = keys.filter((key) => isInBookWindow(todayKey, key));
  return firstBookableDay(inWindow, counts) ?? inWindow[0] ?? todayKey;
}

function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? "" : "s"}`;
}
